/**
 * ПОРОЖДЁННЫЙ ФАЙЛ. Не правьте руками.
 *
 * Источник: app/contracts/openapi.yaml, блок x-seeno-access.
 * Перегенерация: npm run api:types
 *
 * Упорядоченный список правил доступа: первое совпадение выигрывает, умолчание
 * закрытое. Сопоставление путей и методов — в ./access.ts.
 */

/** Роли контракта. `agency_client` видит только своё пространство. */
export type Role = 'owner' | 'analyst' | 'viewer' | 'agency_client';

export const ROLES: readonly Role[] = ['owner', 'analyst', 'viewer', 'agency_client'];

export interface AccessRule {
  /** Шаблоны путей контракта. `**` — один и более сегментов, `{param}` — ровно один. */
  readonly paths: readonly string[];
  /** Методы правила. `*` — любой. */
  readonly methods: readonly string[];
  /** `public` — без токена, `any_member` — любой участник, иначе перечень ролей. */
  readonly roles: 'public' | 'any_member' | readonly Role[];
}

export const ACCESS_RULES: readonly AccessRule[] = [
  {
    paths: ['/healthz', '/readyz'],
    methods: ['GET'],
    roles: 'public',
  },
  {
    paths: ['/public/**'],
    methods: ['*'],
    roles: 'public',
  },
  {
    paths: ['/webhooks/**'],
    methods: ['*'],
    roles: 'public',
  },
  {
    paths: [
      '/auth/signup',
      '/auth/login',
      '/auth/login/mfa',
      '/auth/refresh',
      '/auth/email/verify',
      '/auth/email/verify/resend',
      '/auth/password/forgot',
      '/auth/password/reset/validate',
      '/auth/password/reset/confirm',
      '/auth/yandex/start',
      '/auth/yandex/callback',
      '/auth/recovery',
      '/auth/recovery/{recoveryId}',
    ],
    methods: ['*'],
    roles: 'public',
  },
  {
    paths: [
      '/auth/**',
      '/settings/**',
      '/invitations/accept',
      '/ownership-transfers/{transferId}/accept',
    ],
    methods: ['*'],
    roles: 'any_member',
  },
  {
    paths: ['/organizations'],
    methods: ['GET', 'POST'],
    roles: 'any_member',
  },
  {
    paths: ['/organizations/current/activity'],
    methods: ['GET'],
    roles: ['owner'],
  },
  {
    paths: ['/organizations/current/quotas'],
    methods: ['GET'],
    roles: ['owner', 'analyst', 'viewer'],
  },
  {
    paths: ['/organizations/current', '/organizations/current/white-label'],
    methods: ['GET'],
    roles: ['owner', 'analyst', 'viewer'],
  },
  {
    paths: ['/organizations/current/**', '/organizations/current'],
    methods: ['*'],
    roles: ['owner'],
  },
  {
    paths: [
      '/members/**',
      '/members',
      '/invitations/**',
      '/invitations',
      '/ownership-transfers/**',
    ],
    methods: ['*'],
    roles: ['owner'],
  },
  {
    paths: ['/billing/**'],
    methods: ['*'],
    roles: ['owner'],
  },
  {
    paths: ['/api-tokens', '/api-tokens/**'],
    methods: ['*'],
    roles: ['owner'],
  },
  {
    paths: ['/uploads'],
    methods: ['POST'],
    roles: ['owner'],
  },
  {
    paths: ['/portals', '/portals/**'],
    methods: ['GET'],
    roles: ['owner', 'analyst', 'viewer'],
  },
  {
    paths: ['/portals', '/portals/**'],
    methods: ['*'],
    roles: ['owner'],
  },
  {
    paths: ['/workspaces/{workspaceId}/allocation'],
    methods: ['GET'],
    roles: ['owner', 'analyst', 'viewer'],
  },
  {
    paths: ['/workspaces/{workspaceId}/allocation'],
    methods: ['PUT'],
    roles: ['owner'],
  },
  {
    paths: ['/workspaces', '/workspaces/**'],
    methods: ['GET'],
    roles: ['owner', 'analyst', 'viewer', 'agency_client'],
  },
  {
    paths: ['/workspaces', '/workspaces/**'],
    methods: ['*'],
    roles: ['owner', 'analyst'],
  },
  {
    paths: ['/brands/{brandId}/metrics/memory/questions'],
    methods: ['GET'],
    roles: ['owner', 'analyst', 'viewer'],
  },
  {
    paths: [
      '/brands/{brandId}/questions',
      '/brands/{brandId}/questions/**',
      '/brands/{brandId}/question-revisions',
      '/questions/**',
      '/question-packs',
      '/question-packs/**',
    ],
    methods: ['GET'],
    roles: ['owner', 'analyst', 'viewer'],
  },
  {
    paths: [
      '/brands/{brandId}/questions',
      '/brands/{brandId}/questions/**',
      '/questions/**',
    ],
    methods: ['*'],
    roles: ['owner', 'analyst'],
  },
  {
    paths: ['/runs', '/runs/**', '/brands/{brandId}/readiness'],
    methods: ['GET'],
    roles: ['owner', 'analyst', 'viewer', 'agency_client'],
  },
  {
    paths: [
      '/runs',
      '/runs/**',
      '/brands/{brandId}/metrics/recompute',
      '/brands/{brandId}/readiness',
    ],
    methods: ['*'],
    roles: ['owner', 'analyst'],
  },
  {
    paths: ['/agency/**'],
    methods: ['*'],
    roles: ['owner', 'analyst'],
  },
  {
    paths: ['/brands/{brandId}/schedule'],
    methods: ['GET'],
    roles: ['owner', 'analyst', 'viewer'],
  },
  {
    paths: ['/alert-rules', '/alert-rules/**'],
    methods: ['GET'],
    roles: ['owner', 'analyst', 'viewer'],
  },
  {
    paths: ['/alert-rules', '/alert-rules/**'],
    methods: ['*'],
    roles: ['owner', 'analyst'],
  },
  {
    paths: ['/alerts', '/alerts/**'],
    methods: ['*'],
    roles: ['owner', 'analyst', 'viewer', 'agency_client'],
  },
  {
    paths: ['/reports/{reportId}/share'],
    methods: ['*'],
    roles: ['owner', 'analyst'],
  },
  {
    paths: ['/reports', '/reports/**'],
    methods: ['GET'],
    roles: ['owner', 'analyst', 'viewer', 'agency_client'],
  },
  {
    paths: ['/reports', '/reports/**'],
    methods: ['*'],
    roles: ['owner', 'analyst', 'viewer'],
  },
  {
    paths: ['/audits/claim'],
    methods: ['POST'],
    roles: ['owner', 'analyst'],
  },
  {
    paths: ['/audits'],
    methods: ['GET'],
    roles: ['owner', 'analyst', 'viewer'],
  },
  {
    paths: ['/engines', '/portfolio'],
    methods: ['GET'],
    roles: ['owner', 'analyst', 'viewer', 'agency_client'],
  },
  {
    paths: ['/answers/{answerId}'],
    methods: ['GET'],
    roles: ['owner', 'analyst', 'viewer', 'agency_client'],
  },
  {
    paths: [
      '/brands',
      '/brands/**',
      '/competitors/**',
      '/recommendations/**',
      '/action-plans/**',
    ],
    methods: ['GET'],
    roles: ['owner', 'analyst', 'viewer', 'agency_client'],
  },
  {
    paths: [
      '/brands',
      '/brands/**',
      '/competitors/**',
      '/recommendations/**',
      '/action-plans/**',
    ],
    methods: ['*'],
    roles: ['owner', 'analyst'],
  },
];
